/* src/app/components/ai-assist/bot/bot/bot.component.scss */
:host {
  display: block;
  width: 100%;
  min-width: 0;
  color: #202b3c;
  font-family:
    Inter,
    "Segoe UI",
    Arial,
    sans-serif;
  --chat-primary: #253b80;
  --chat-primary-dark: #1c2e68;
  --chat-primary-light: #edf2ff;
  --chat-text: #263247;
  --chat-muted: #778197;
  --chat-border: #e6eaf1;
  --chat-surface: #ffffff;
  --chat-background: #f7f9fc;
  --chat-success: #16845b;
  --chat-danger: #d34a56;
  --chat-radius: 12px;
  --chat-transition: all 0.2s ease;
  --font-xs: 11px;
  --font-sm: 12px;
  --font-md: 13px;
  --font-base: 15px;
  --font-lg: 16px;
  --font-xl: 18px;
  --font-2xl: 21px;
  --font-3xl: 24px;
  --line-base: 1.8;
}
*,
*::before,
*::after {
  box-sizing: border-box;
}
.chat-list-row {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  margin: 16px 0;
  padding: 0 16px;
}
.question-row {
  align-items: flex-end;
}
.answer-row {
  align-items: stretch;
}
.question-card {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  width: 100%;
  min-width: 0;
  padding: 17px 20px;
  border: 1px solid #e0e7ff;
  border-radius: 18px 18px 5px 18px;
  background:
    linear-gradient(
      135deg,
      #f1f4ff,
      #f8f9ff);
  box-shadow: 0 3px 12px rgba(37, 59, 128, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.question-card.loading {
  border-color: #bdc9fa;
  box-shadow: 0 0 0 3px rgba(37, 59, 128, 0.05);
}
.question-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  border-radius: 12px;
  background: var(--chat-primary);
  color: #fff;
  font-size: 16px;
}
.question-body {
  flex: 1;
  min-width: 0;
}
.message-label {
  margin-bottom: 6px;
  color: var(--chat-muted);
  font-size: var(--font-xs);
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}
.question-text {
  color: var(--chat-text);
  font-size: var(--font-base);
  font-weight: 500;
  line-height: var(--line-base);
  overflow-wrap: anywhere;
}
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 12px;
  color: var(--chat-muted);
}
.typing-indicator span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--chat-primary);
  animation: typing-bounce 1s infinite ease-in-out;
}
.typing-indicator span:nth-child(2) {
  animation-delay: 0.15s;
}
.typing-indicator span:nth-child(3) {
  animation-delay: 0.3s;
}
.typing-indicator small {
  margin-left: 6px;
  font-size: var(--font-sm);
}
@keyframes typing-bounce {
  0%, 60%, 100% {
    opacity: 0.4;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-4px);
  }
}
.answer-card {
  width: 100%;
  min-width: 0;
  padding: 22px 24px 16px;
  overflow-wrap: anywhere;
  border: 1px solid var(--chat-border);
  border-radius: 18px;
  background: var(--chat-surface);
  box-shadow: 0 4px 18px rgba(29, 43, 76, 0.04);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.answer-card:hover {
  border-color: #dce3f0;
  box-shadow: 0 7px 24px rgba(29, 43, 76, 0.06);
}
.answer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  padding-bottom: 17px;
}
.assistant-identity {
  display: flex;
  align-items: center;
  gap: 11px;
  min-width: 0;
}
.assistant-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border: 1px solid #dce5ff;
  border-radius: 14px;
  background:
    linear-gradient(
      145deg,
      #edf1ff,
      #e5f4ff);
  color: var(--chat-primary);
  font-size: 19px;
}
.assistant-name {
  color: #202c43;
  font-size: var(--font-md);
  font-weight: 700;
}
.assistant-subtitle {
  margin-top: 4px;
  color: var(--chat-muted);
  font-size: var(--font-xs);
}
.answer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 5px;
}
.icon-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  color: #738097;
  font-size: 15px;
  text-decoration: none;
  cursor: pointer;
  transition: var(--chat-transition);
}
.icon-action:hover {
  border-color: #e0e7ff;
  background: var(--chat-primary-light);
  color: var(--chat-primary);
}
.icon-action:focus-visible {
  outline: 2px solid var(--chat-primary);
  outline-offset: 2px;
}
.icon-action.is-pinned {
  background: #fff5dc;
  color: #a36b00;
}
.icon-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.audio-action app-toggle-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}
.answer-content {
  min-width: 0;
  padding: 21px 0;
  color: #344157;
  font-size: var(--font-base);
  line-height: var(--line-base);
  overflow-wrap: anywhere;
  word-break: normal;
}
.answer-content > :first-child {
  margin-top: 0;
}
.answer-content > :last-child {
  margin-bottom: 0;
}
.answer-content p {
  margin: 0 0 15px;
  font-size: inherit;
  line-height: inherit;
}
.answer-content h1,
.answer-content h2,
.answer-content h3,
.answer-content h4 {
  margin: 24px 0 12px;
  color: #202d44;
  font-weight: 700;
  line-height: 1.4;
}
.answer-content h1 {
  font-size: var(--font-3xl);
}
.answer-content h2 {
  font-size: var(--font-2xl);
}
.answer-content h3 {
  font-size: var(--font-xl);
}
.answer-content h4 {
  font-size: var(--font-lg);
}
.answer-content ul,
.answer-content ol {
  margin: 12px 0;
  padding-left: 25px;
}
.answer-content li {
  margin-bottom: 7px;
  font-size: inherit;
  line-height: 1.75;
}
.answer-content blockquote {
  margin: 16px 0;
  padding: 12px 16px;
  border-left: 4px solid #a8b8ed;
  border-radius: 0 8px 8px 0;
  background: #f6f8ff;
  color: #52617a;
  font-size: inherit;
  line-height: 1.8;
}
.answer-content pre {
  max-width: 100%;
  margin: 16px 0;
  padding: 16px;
  overflow-x: auto;
  border: 1px solid #e2e7f0;
  border-radius: 10px;
  background: #f6f8fc;
  color: #24334b;
  font-size: var(--font-sm);
  line-height: 1.7;
  white-space: pre;
  -webkit-overflow-scrolling: touch;
}
.answer-content code {
  padding: 2px 5px;
  border-radius: 5px;
  background: #f0f3f9;
  color: #354b91;
  font-size: 0.9em;
  overflow-wrap: anywhere;
}
.answer-content pre code {
  padding: 0;
  background: transparent;
  color: inherit;
  overflow-wrap: normal;
}
.answer-content a {
  color: #3157b8;
  text-decoration: underline;
  text-underline-offset: 3px;
  overflow-wrap: anywhere;
}
.answer-content table {
  display: block;
  width: 100%;
  max-width: 100%;
  margin: 16px 0;
  overflow-x: auto;
  border-collapse: collapse;
  font-size: 14px;
  -webkit-overflow-scrolling: touch;
}
.answer-content th,
.answer-content td {
  min-width: 100px;
  padding: 11px 13px;
  border: 1px solid #e3e8f1;
  text-align: left;
  vertical-align: top;
}
.answer-content th {
  background: #f3f6fc;
  color: #263650;
  font-weight: 700;
}
.answer-content img,
.answer-content video,
.answer-content iframe {
  max-width: 100%;
}
.answer-content img {
  height: auto;
  border-radius: 10px;
}
.answer-content hr {
  margin: 20px 0;
  border: 0;
  border-top: 1px solid #e7ebf2;
}
.source-document {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 4px;
  padding: 11px 13px;
  color: #66748c;
  font-size: var(--font-md);
}
.source-document > i {
  color: var(--chat-primary);
  font-size: 15px;
}
.source-document > span:first-of-type {
  color: #35445d;
  font-weight: 700;
}
.source-document > span:last-of-type {
  overflow-wrap: anywhere;
}
.source-section {
  margin-top: 13px;
  padding-top: 12px;
  border-top: 1px solid #edf0f5;
}
.source-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 11px;
  border: 1px solid #dce5fb;
  border-radius: 8px;
  background: #f4f7ff;
  color: var(--chat-primary);
  font-size: var(--font-md);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}
.source-toggle:hover {
  background: #e9efff;
}
.source-toggle:focus-visible {
  outline: 2px solid var(--chat-primary);
  outline-offset: 2px;
}
.source-excerpt {
  margin-top: 12px;
  padding: 15px;
  border: 1px solid #e6eaf2;
  border-radius: 10px;
  background: #fafbfd;
  color: #5b667a;
  font-size: 14px;
  line-height: 1.8;
  overflow-wrap: anywhere;
}
.answer-question {
  margin-top: 18px;
  padding: 13px 15px;
  border-left: 3px solid #8da3ec;
  border-radius: 0 9px 9px 0;
  background: #f6f8ff;
}
.answer-question > span {
  color: #74819b;
  font-size: var(--font-xs);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.answer-question p {
  margin: 6px 0 0;
  color: #2d3c56;
  font-size: var(--font-md);
  line-height: 1.75;
}
.answer-question + .answer-content {
  padding-top: 12px;
}
.feedback-section {
  margin-top: 8px;
  padding-top: 17px;
  border-top: 1px solid #eef0f5;
}
.feedback-label {
  display: block;
  margin-bottom: 11px;
  color: #778197;
  font-size: var(--font-md);
  font-weight: 600;
}
.feedback-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 9px;
}
.feedback-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 35px;
  padding: 8px 12px;
  border: 1px solid #e2e7ef;
  border-radius: 9px;
  background: #fff;
  color: #65728a;
  font-size: var(--font-md);
  font-weight: 500;
  cursor: pointer;
  transition: var(--chat-transition);
}
.feedback-button i {
  font-size: 14px;
}
.feedback-button:hover {
  border-color: #b9c8f5;
  background: #f5f7ff;
  color: var(--chat-primary);
}
.feedback-button.selected {
  border-color: #a8baf5;
  background: #edf2ff;
  color: var(--chat-primary);
}
.feedback-button:focus-visible {
  outline: 2px solid var(--chat-primary);
  outline-offset: 2px;
}
.feedback-form {
  margin-top: 15px;
  padding: 16px;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  background: #fafbfe;
}
.feedback-form label {
  display: block;
  margin-bottom: 8px;
  color: #344157;
  font-size: var(--font-md);
  font-weight: 700;
}
.feedback-form textarea {
  display: block;
  width: 100%;
  min-height: 90px;
  padding: 12px;
  resize: vertical;
  border: 1px solid #dce2ed;
  border-radius: 9px;
  outline: none;
  background: #fff;
  color: var(--chat-text);
  font-family: inherit;
  font-size: var(--font-md);
  line-height: 1.6;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.feedback-form textarea:focus {
  border-color: #8399e5;
  box-shadow: 0 0 0 3px rgba(37, 59, 128, 0.08);
}
.feedback-form textarea::placeholder {
  color: #a2aabc;
}
.feedback-form-actions {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 12px;
}
.btn-cancel,
.btn-submit,
.btn-export {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 9px 14px;
  border: 1px solid transparent;
  border-radius: 9px;
  font-family: inherit;
  font-size: var(--font-md);
  font-weight: 600;
  cursor: pointer;
  transition: var(--chat-transition);
}
.btn-cancel:disabled,
.btn-submit:disabled,
.btn-export:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.btn-cancel:focus-visible,
.btn-submit:focus-visible,
.btn-export:focus-visible {
  outline: 2px solid var(--chat-primary);
  outline-offset: 2px;
}
.btn-cancel {
  border-color: #dfe4ed;
  background: #fff;
  color: #647087;
}
.btn-cancel:hover:not(:disabled) {
  background: #f2f4f8;
}
.btn-submit {
  background: var(--chat-primary);
  color: #fff;
}
.btn-submit:hover:not(:disabled) {
  background: var(--chat-primary-dark);
  box-shadow: 0 3px 10px rgba(37, 59, 128, 0.18);
}
.btn-export {
  border-color: #dce4f5;
  background: #f7f9ff;
  color: var(--chat-primary);
}
.btn-export:hover:not(:disabled) {
  border-color: #b7c7f2;
  background: #edf2ff;
}
.message-timestamp {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 14px;
  padding-top: 11px;
  border-top: 1px solid #f0f2f6;
  color: #9aa3b4;
  font-size: var(--font-xs);
}
.message-timestamp i {
  font-size: var(--font-xs);
}
.pagination-loading-space {
  width: 100%;
  height: 50px;
}
.answer-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
  background: rgba(17, 27, 49, 0.55);
  opacity: 0;
  visibility: hidden;
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
  transition: opacity 0.2s ease, visibility 0.2s ease;
}
.answer-modal-backdrop.visible {
  opacity: 1;
  visibility: visible;
}
.answer-modal {
  display: flex;
  flex-direction: column;
  width: min(100%, 1050px);
  max-height: calc(100dvh - 48px);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.2);
  animation: modal-enter 0.22s ease-out;
}
.answer-modal.fullscreen {
  position: fixed;
  inset: 12px;
  width: auto;
  height: auto;
  max-height: none;
  border-radius: 12px;
}
@keyframes modal-enter {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.99);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.modal-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  padding: 19px 23px;
  border-bottom: 1px solid #e9edf4;
  background: #fff;
}
.modal-heading {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.modal-heading h2 {
  margin: 0;
  color: #202c43;
  font-size: var(--font-lg);
  font-weight: 700;
}
.modal-heading p {
  margin: 5px 0 0;
  color: #8791a5;
  font-size: var(--font-xs);
  line-height: 1.5;
}
.modal-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #edf2ff;
  color: var(--chat-primary);
  font-size: 18px;
}
.modal-header-actions {
  display: flex;
  flex-shrink: 0;
  gap: 6px;
}
.modal-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px 23px;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: #fafbfd;
}
.editor-container {
  min-height: 240px;
  padding: 15px;
  border: 1px solid #e5e9f1;
  border-radius: 10px;
  background: #fff;
}
.editor-container #toolbar_ {
  min-height: 0;
}
.modal-footer {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: 17px;
  padding: 19px 23px;
  border-top: 1px solid #e9edf4;
  background: #fff;
}
.file-name-field {
  width: 100%;
}
.file-name-field label {
  display: block;
  margin-bottom: 8px;
  color: #39465d;
  font-size: var(--font-md);
  font-weight: 700;
}
.file-name-field input {
  display: block;
  width: 100%;
  min-height: 42px;
  padding: 10px 12px;
  border: 1px solid #dce2ed;
  border-radius: 9px;
  outline: none;
  background: #fff;
  color: #27344b;
  font-family: inherit;
  font-size: var(--font-md);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.file-name-field input:focus {
  border-color: #8298e3;
  box-shadow: 0 0 0 3px rgba(37, 59, 128, 0.08);
}
.file-name-field input::placeholder {
  color: #a2aabc;
}
.field-error {
  display: block;
  margin-top: 6px;
  color: var(--chat-danger);
  font-size: var(--font-xs);
}
.modal-footer-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 9px;
}
@media (max-width: 768px) {
  .chat-list-row {
    margin: 12px 0;
    padding: 0 10px;
  }
  .question-card {
    max-width: 100%;
    min-width: 0;
    padding: 14px;
    gap: 10px;
  }
  .question-icon {
    flex-basis: 34px;
    width: 34px;
    height: 34px;
    border-radius: 10px;
  }
  .answer-card {
    padding: 16px 14px 13px;
    border-radius: 14px;
  }
  .answer-header {
    align-items: flex-start;
    gap: 12px;
  }
  .answer-actions {
    width: 100%;
    justify-content: flex-start;
    padding-top: 8px;
    border-top: 1px solid #f0f2f6;
  }
  .answer-content {
    padding: 17px 0;
    font-size: 14px;
    line-height: 1.8;
  }
  .answer-content h1 {
    font-size: 22px;
  }
  .answer-content h2 {
    font-size: 19px;
  }
  .answer-content h3 {
    font-size: 17px;
  }
  .answer-content h4 {
    font-size: 15px;
  }
  .answer-content table {
    font-size: 13px;
  }
  .source-excerpt {
    font-size: 13px;
  }
  .modal-footer-actions {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .modal-footer-actions button {
    width: 100%;
  }
}
@media (max-width: 480px) {
  .question-card {
    max-width: 100%;
  }
  .question-text {
    font-size: 14px;
  }
  .assistant-avatar {
    flex-basis: 36px;
    width: 36px;
    height: 36px;
  }
  .answer-actions {
    gap: 2px;
  }
  .icon-action {
    flex-basis: 32px;
    width: 32px;
    height: 32px;
  }
  .answer-content {
    font-size: 14px;
    line-height: 1.75;
  }
  .answer-content h1 {
    font-size: 20px;
  }
  .answer-content h2 {
    font-size: 18px;
  }
  .answer-content h3 {
    font-size: 16px;
  }
  .answer-content h4 {
    font-size: 15px;
  }
  .answer-content table {
    font-size: 13px;
  }
  .source-excerpt {
    font-size: 13px;
  }
  .feedback-actions {
    gap: 7px;
  }
  .feedback-button {
    padding: 8px 10px;
    font-size: var(--font-sm);
  }
  .feedback-form {
    padding: 12px;
  }
  .feedback-form-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .answer-modal-backdrop {
    align-items: stretch;
    padding: 8px;
  }
  .answer-modal {
    width: 100%;
    max-height: calc(100dvh - 16px);
    border-radius: 12px;
  }
  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 14px;
  }
  .modal-heading h2 {
    font-size: 15px;
  }
  .modal-heading p {
    font-size: 10px;
  }
  .modal-footer-actions {
    grid-template-columns: 1fr;
  }
}
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
/*# sourceMappingURL=bot.component.css.map */
